/* ==========================================================================
   Kopstorm Cart Drawer v2 - kopstorm-cart.js
   Anavrin / byanavrin.com

   Behaviour for the native drawer that replaces the third party cart app.
   Three jobs, nothing else:

     1. kop-cart-timer   reservation countdown, persisted for the session
     2. upsell add       add to cart from inside the drawer, then re-render
     3. cart link router send /cart clicks to the drawer instead of the page
     4. add watcher     any completed /cart/add request, from the theme or an
                        app, refreshes and opens this drawer
     5. kop-w86 arm   when <html> carries kop-w86 (Intelligems control
                        arm for a native versus app test) jobs 2 to 4 stand
                        down and the app drawer runs as before

   The markup is re-rendered wholesale by the theme every time the cart
   changes, so element behaviour lives in a custom element (re-runs on its
   own) and button behaviour lives in a delegated document listener (survives
   the swap). No dependencies beyond what Concept already exposes on
   window.theme.
   ========================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'kopstorm:cart-reserve';
  var root = document.documentElement;

  function appArm() {
    return root.classList.contains('kop-w86');
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function readDeadline() {
    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      return raw ? parseInt(raw, 10) : 0;
    } catch (error) {
      return 0;
    }
  }

  function writeDeadline(value) {
    try {
      window.sessionStorage.setItem(STORE_KEY, String(value));
    } catch (error) {
      /* Private mode, nothing to persist. The timer still runs in memory. */
    }
  }

  /* ------------------------------------------------------------------------
     1. Reservation countdown
     The deadline is stored per session so the clock keeps running across page
     loads and drawer re-renders rather than resetting to the full duration
     every time the shopper opens the cart.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kop-cart-timer')) {
    customElements.define(
      'kop-cart-timer',
      class extends HTMLElement {
        connectedCallback() {
          this.clock = this.querySelector('[data-kop-clock]');
          if (!this.clock) return;

          this.minutes = parseInt(this.getAttribute('data-minutes'), 10) || 10;
          this.onExpiry = this.getAttribute('data-expiry') || 'restart';
          this.reset = this.getAttribute('data-reset') || 'session';
          this.duration = this.minutes * 60 * 1000;

          this.start();
          this.interval = window.setInterval(this.tick.bind(this), 1000);

          /* In 'open' mode the clock restarts every time the drawer is shown.
             The drawer element toggles aria-expanded on its overlay and gains
             the open attribute, so that attribute is what is watched. */
          if (this.reset === 'open') {
            var drawer = this.closest('cart-drawer');
            if (drawer) {
              this.observer = new MutationObserver(
                function (records) {
                  for (var i = 0; i < records.length; i++) {
                    if (drawer.hasAttribute('open')) {
                      this.start(true);
                      return;
                    }
                  }
                }.bind(this)
              );
              this.observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
              if (drawer.hasAttribute('open')) this.start(true);
            }
          }
        }

        /* A stored deadline is reused only when it is still in the future and
           no longer than one full duration away. Anything else is stale, from a
           longer setting or an expired run, and starts over. */
        start(force) {
          var now = Date.now();
          var deadline = this.reset === 'open' ? 0 : readDeadline();

          if (force || !deadline || deadline <= now || deadline - now > this.duration) {
            deadline = now + this.duration;
            writeDeadline(deadline);
          }

          this.deadline = deadline;
          this.hidden = false;
          this.tick();
        }

        disconnectedCallback() {
          window.clearInterval(this.interval);
          if (this.observer) this.observer.disconnect();
        }

        tick() {
          var remaining = this.deadline - Date.now();

          if (remaining <= 0) {
            if (this.onExpiry === 'hide') {
              window.clearInterval(this.interval);
              this.hidden = true;
              return;
            }
            this.deadline = Date.now() + this.duration;
            writeDeadline(this.deadline);
            remaining = this.duration;
          }

          var total = Math.floor(remaining / 1000);
          var text = pad(Math.floor(total / 60)) + ':' + pad(total % 60);
          if (this.clock.textContent !== text) this.clock.textContent = text;
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     2. Upsell add to cart
     /cart/add.js returns the added lines, not the cart, so the cart is read
     back straight after and the rendered section is attached to it. That
     single publish updates the drawer body and the header count together,
     which is what the theme's own cart-items and cart-count subscribe to.
     ------------------------------------------------------------------------ */
  function bundledSections(fallbackId) {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    if (!sections.length && fallbackId) sections.push(fallbackId);
    return sections;
  }

  function publishCart(cart) {
    var pubsub = window.theme && window.theme.pubsub;
    if (pubsub && pubsub.publish && pubsub.PUB_SUB_EVENTS) {
      pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'kopstorm-cart', cart: cart });
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
  }

  function addToCart(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;

    var sections = bundledSections(button.getAttribute('data-section-id'));
    button.setAttribute('aria-busy', 'true');

    fetch((window.theme && window.theme.routes ? window.theme.routes.cart_add_url : '/cart/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (added) {
        if (added && added.status) throw new Error(added.description || added.message);
        return fetch((window.theme && window.theme.routes ? window.theme.routes.cart_url : '/cart') + '.js')
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            cart.sections = (added && added.sections) || {};
            publishCart(cart);
            root.dispatchEvent(new CustomEvent('kopstorm:cart:add', { detail: { variantId: variantId, source: button.classList.contains('kop-cart__add--kit') ? 'kit-rail' : 'upsell' } }));
          });
      })
      .catch(function (error) {
        console.error('[kopstorm-cart]', error);
        button.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-kop-cart-add]');
    if (!button || appArm()) return;
    event.preventDefault();
    addToCart(button);
  });

  /* ------------------------------------------------------------------------
     2b. Upsell slider and bundle upgrade (17 Sep 2026)
     The slider is a custom element so it re-arms after every drawer
     re-render. The upgrade adds the bundle first and only then takes the
     matching kits out, with the drawer sections attached to that last write.
     ------------------------------------------------------------------------ */
  if (!customElements.get('kop-cart-slider')) {
    customElements.define(
      'kop-cart-slider',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-kop-slider-track]');
          this.buttons = this.querySelectorAll('[data-kop-slide]');
          if (!this.track || !this.buttons.length) return;
          this.onScroll = this.sync.bind(this);
          this.onClick = this.move.bind(this);
          this.track.addEventListener('scroll', this.onScroll, { passive: true });
          this.buttons.forEach(function (b) { b.addEventListener('click', this.onClick); }, this);
          /* The drawer renders this while closed (zero width), and images
             change the track width as they load, so a one-off measure left the
             arrows disabled. Re-measure whenever the track or its content
             resizes, and on every image load. */
          if (window.ResizeObserver) {
            this.ro = new ResizeObserver(this.onScroll);
            this.ro.observe(this.track);
            Array.prototype.forEach.call(this.track.children, function (li) { this.ro.observe(li); }, this);
          }
          this.track.querySelectorAll('img').forEach(function (img) {
            if (!img.complete) img.addEventListener('load', this.onScroll, { once: true });
          }, this);
          requestAnimationFrame(this.onScroll);
        }
        disconnectedCallback() {
          if (this.ro) this.ro.disconnect();
          if (!this.track) return;
          this.track.removeEventListener('scroll', this.onScroll);
          this.buttons.forEach(function (b) { b.removeEventListener('click', this.onClick); }, this);
        }
        move(event) {
          var dir = parseInt(event.currentTarget.getAttribute('data-kop-slide'), 10) || 1;
          var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
        }
        sync() {
          var width = this.track.clientWidth;
          /* Not laid out yet (drawer closed): keep next enabled, measure later. */
          if (!width) return;
          var max = this.track.scrollWidth - width - 2;
          var x = Math.abs(this.track.scrollLeft);
          this.buttons.forEach(function (b) {
            var dir = parseInt(b.getAttribute('data-kop-slide'), 10);
            b.disabled = dir < 0 ? x <= 2 : max <= 0 || x >= max;
          });
          this.classList.toggle('is-static', max <= 0);
        }
      }
    );
  }

  function upgradeBundle(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;
    var updates;
    try {
      updates = JSON.parse(button.getAttribute('data-updates') || '{}');
    } catch (e) {
      return;
    }
    var routes = (window.theme && window.theme.routes) || {};
    var sections = bundledSections(button.getAttribute('data-section-id'));
    button.setAttribute('aria-busy', 'true');

    fetch(routes.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (response) { return response.json(); })
      .then(function (added) {
        if (added && added.status) throw new Error(added.description || added.message);
        return fetch(routes.cart_update_url || '/cart/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates, sections: sections })
        });
      })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (cart && cart.status) throw new Error(cart.description || cart.message);
        publishCart(cart);
        root.dispatchEvent(new CustomEvent('kopstorm:cart:add', { detail: { variantId: variantId, source: 'bundle-upgrade' } }));
      })
      .catch(function (error) {
        console.error('[kopstorm-cart]', error);
        button.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-kop-upgrade]');
    if (!button || appArm()) return;
    event.preventDefault();
    upgradeBundle(button);
  });

  /* ------------------------------------------------------------------------
     3. Cart link router
     Anything pointing at the cart page opens the drawer instead. The theme's
     own aria-controls buttons are left alone: they already work, and
     intercepting them would double handle the click.
     ------------------------------------------------------------------------ */
  document.addEventListener(
    'click',
    function (event) {
      if (appArm()) return;
      var link = event.target.closest && event.target.closest('a[href$="/cart"], a[href*="/cart?"]');
      if (!link || link.hasAttribute('aria-controls')) return;

      var drawer = document.getElementById('CartDrawer');
      if (!drawer || !drawer.classList.contains('kop-cart-drawer')) return;

      event.preventDefault();
      if (typeof drawer.show === 'function') drawer.show();
      else drawer.setAttribute('open', '');
    },
    true
  );

  /* ------------------------------------------------------------------------
     4. Open after ANY add to cart
     UpCart binds its own handler to the add to cart button and runs its own
     /cart/add.js, so the theme's product-form submit never fires and nothing
     opens this drawer (the app drawer opens, hidden). Rather than fight the
     app for the click, watch the network: whenever a /cart/add request
     completes, from the theme, an app or a bundle widget, refresh this drawer
     from the section and open it. A short delay lets the theme's own path
     (product-form shows the drawer itself) win first, so it never opens twice.
     ------------------------------------------------------------------------ */
  function nativeActive() {
    var drawer = document.getElementById('CartDrawer');
    return !!drawer && drawer.classList.contains('kop-cart-drawer') && !appArm();
  }

  function isCartAdd(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url.split('#')[0]);
  }

  /* Open net. An add that carries its own sections is expected to open the
     drawer itself (the theme's product-form calls cartDrawer.show). When an
     app owns the click, or the theme's promise chain never reaches that line,
     nothing opens. Check once, late enough that the normal path has already
     won, and only act if the drawer is still closed, so a healthy add still
     opens exactly once. */
  var ensureTimer = 0;
  function ensureOpen() {
    window.clearTimeout(ensureTimer);
    ensureTimer = window.setTimeout(function () {
      if (!nativeActive()) return;
      var drawer = document.getElementById('CartDrawer');
      if (!drawer || drawer.hasAttribute('open')) return;
      if (typeof drawer.show === 'function') drawer.show();
      else drawer.setAttribute('open', '');
    }, 700);
  }

  /* Recovery net. A drawer that was SERVED with an empty cart has no line item
     markup, and the theme repaints #MiniCart from a cartUpdate only through
     cart-items. If an add that carried its own sections still leaves the
     drawer on its empty state while the cart holds something, refresh it once
     from the section. It can only fire in that broken state, so a healthy add
     still paints exactly once and nothing flickers. */
  var recoverTimer = 0;
  function recoverIfEmpty() {
    window.clearTimeout(recoverTimer);
    recoverTimer = window.setTimeout(function () {
      if (!nativeActive()) return;
      var drawer = document.getElementById('CartDrawer');
      if (!drawer.querySelector('.kop-cart__empty')) return;
      if (cartCount() <= 0) return;
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { open: !drawer.hasAttribute('open') }
      }));
    }, 450);
  }

  var openTimer = 0;
  function openAfterAdd() {
    window.clearTimeout(openTimer);
    openTimer = window.setTimeout(function () {
      if (!nativeActive()) return;
      var drawer = document.getElementById('CartDrawer');
      if (drawer.hasAttribute('open')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    }, 150);
  }

  if (window.fetch && !window.fetch.__kopCart) {
    var nativeFetch = window.fetch;
    /* A sender that asks Shopify for rendered sections redraws and opens the
       drawer itself (the theme's product-form, the bundle form, our own rail
       add). Refreshing after it meant a second fetch and a second paint of the
       same drawer, a beat later. Only an add without sections needs a hand. */
    var carriesSections = function (body) {
      if (!body) return false;
      try {
        if (typeof body === 'string') return /"sections"\s*:|(^|&)sections(%5B%5D|\[\])?=/.test(body);
        if (typeof body.has === 'function') return body.has('sections') || body.has('sections[]');
      } catch (e) { /* unreadable body */ }
      return false;
    };
    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = nativeFetch.apply(this, arguments);
      if (isCartAdd(url)) {
        var withSections = carriesSections(init && init.body);
        promise.then(function (response) {
          if (!response || !response.ok) return;
          if (withSections) { recoverIfEmpty(); ensureOpen(); }
          else openAfterAdd();
        }, function () { /* add failed, nothing to open */ });
      }
      return promise;
    };
    wrapped.__kopCart = true;
    window.fetch = wrapped;
  }

  if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__kopCart) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartAdd(String(url))) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) openAfterAdd();
        });
      }
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.__kopCart = true;
  }

  /* Fallback for an app that captured window.fetch before this file ran, or
     that binds its own handler to the button so the theme's product-form
     submit never fires: note the cart count when an add button is clicked,
     then compare a moment later. A changed count with the drawer still closed
     means an add went past the watchers above.

     Matched on the form's action, not on data-type="add-to-cart-form". The
     PDP buy button carries that attribute and the product card quick add
     forms did not, which is why a card add could complete with the drawer
     never opening while the PDP was rescued. */
  function cartCount() {
    var el = document.querySelector('cart-count');
    return el ? parseInt(el.textContent, 10) || 0 : -1;
  }

  function isAddForm(form) {
    if (!form) return false;
    if (form.getAttribute('data-type') === 'add-to-cart-form') return true;
    /* getAttribute, never form.action: a control named action shadows the
       property on HTMLFormElement. */
    var action = form.getAttribute('action') || '';
    return /\/cart\/add/.test(action);
  }

  document.addEventListener('click', function (event) {
    if (!nativeActive()) return;
    var button = event.target.closest && event.target.closest('button[type="submit"], input[type="submit"]');
    if (!button) return;
    var form = button.form || button.closest('form');
    if (!isAddForm(form)) return;

    var before = cartCount();
    window.setTimeout(function () {
      var drawer = document.getElementById('CartDrawer');
      if (!drawer || drawer.hasAttribute('open')) return;
      fetch((window.theme && window.theme.routes ? window.theme.routes.cart_url : '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && cart.item_count !== before) openAfterAdd();
        })
        .catch(function () { /* nothing to do */ });
    }, 1400);
  }, true);

  /* ------------------------------------------------------------------------
     4b. Gamified progress bar (kop_cart_bar_game, 15 Oct 2026)
     kop-cart-game is rendered inside the rewards band on every drawer render
     and carries the reached step count, the rail fill and the shipping level.
     It compares them with the previous render (kept in memory for the page,
     so the first render after a page load only records the state and never
     animates), then:
       - lets the rail grow from the old fill to the new one
       - marks steps reached in this render .is-new (check pop, label fade)
       - marks the shipping line .is-new when a shipping level was reached
       - counts the "You save" amount up from the old value to the new one
     Reduced motion: state is recorded, nothing animates.
     ------------------------------------------------------------------------ */
  var gameState = null;

  function parseCents(el) {
    var raw = el ? parseInt(el.getAttribute('data-kop-cents'), 10) : 0;
    return isNaN(raw) ? 0 : raw;
  }

  /* Formats cents in the same shape as the amount Liquid printed, so the
     count works in every market currency without a money format string:
     the separators, decimals and currency text are read off the final text. */
  function moneyShape(text) {
    var match = text.match(/\d[\d.,\s  ']*\d|\d/);
    if (!match) return null;
    var number = match[0];
    var tail = number.match(/[.,](\d{1,2})$/);
    var decimals = tail ? tail[1].length : 0;
    var decimalSep = tail ? number.charAt(number.length - decimals - 1) : '';
    var body = tail ? number.slice(0, number.length - decimals - 1) : number;
    var group = body.match(/[^\d]/);
    return {
      before: text.slice(0, match.index),
      after: text.slice(match.index + number.length),
      decimals: decimals,
      decimalSep: decimalSep,
      groupSep: group ? group[0] : ''
    };
  }

  function formatShape(shape, cents) {
    var whole = Math.floor(cents / 100);
    var frac = String(cents % 100);
    if (frac.length < 2) frac = '0' + frac;
    var digits = String(whole);
    if (shape.groupSep) digits = digits.replace(/\B(?=(\d{3})+(?!\d))/g, shape.groupSep);
    var out = digits;
    if (shape.decimals) out += shape.decimalSep + frac.slice(0, shape.decimals);
    return shape.before + out + shape.after;
  }

  function countUp(el, from, to) {
    var finalText = el.textContent;
    var shape = moneyShape(finalText);
    if (!shape || from === to) return;
    var start = null;
    var duration = 700;
    el.classList.add('is-counting');
    el.textContent = formatShape(shape, from);
    function frame(now) {
      if (!el.isConnected) return;
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      if (p < 1) {
        el.textContent = formatShape(shape, Math.round(from + (to - from) * eased));
        window.requestAnimationFrame(frame);
      } else {
        el.textContent = finalText;
        el.classList.remove('is-counting');
      }
    }
    window.setTimeout(function () { window.requestAnimationFrame(frame); }, 250);
  }

  if (!customElements.get('kop-cart-game')) {
    customElements.define(
      'kop-cart-game',
      class extends HTMLElement {
        connectedCallback() {
          var cart = this.closest('.kop-cart') || document;
          var saved = cart.querySelector('.kop-cart__saved-value');
          var now = {
            reached: parseInt(this.getAttribute('data-reached'), 10) || 0,
            progress: parseFloat(this.getAttribute('data-progress')) || 0,
            ship: parseInt(this.getAttribute('data-ship'), 10) || 0,
            saved: parseCents(saved)
          };
          var before = gameState;
          gameState = now;
          if (!before) return;
          if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

          var band = this.parentNode;
          var rail = band && band.querySelector('.kop-cart__rail');
          if (rail && before.progress !== now.progress) {
            var target = rail.style.getPropertyValue('--kop-cart-progress');
            rail.style.setProperty('--kop-cart-progress', before.progress + '%');
            void rail.offsetWidth;
            window.requestAnimationFrame(function () {
              rail.style.setProperty('--kop-cart-progress', target);
            });
          }

          if (band && now.reached > before.reached) {
            var steps = band.querySelectorAll('.kop-cart__step');
            var order = 0;
            for (var i = before.reached; i < now.reached && i < steps.length; i++) {
              steps[i].style.setProperty('--kop-game-delay', (0.35 + order * 0.15) + 's');
              steps[i].classList.add('is-new');
              order++;
            }
          }

          if (band && now.ship > before.ship) {
            var line = band.querySelector('.kop-cart__shipline');
            if (line) line.classList.add('is-new');
          }

          if (saved && now.saved > before.saved) countUp(saved, before.saved, now.saved);
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     5. Expose the arm switches for QA. KopstormCRO.enable('w82')
     is the Intelligems path; this is only a console convenience.
     ------------------------------------------------------------------------ */
  window.KopstormCart = window.KopstormCart || {
    arms: { w82: 'percent framing on the kit rail', w83: 'kit rail off', w84: 'accessory cards off', w85: 'reservation timer off', w86: 'app drawer instead of the native drawer' },
    set: function (id, on) {
      if (!this.arms[id]) return false;
      if (window.KopstormCRO) window.KopstormCRO.set(id, on !== false);
      else root.classList.toggle('kop-' + id, on !== false);
      return root.classList.contains('kop-' + id);
    }
  };
})();